import React from 'react';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';
import { FormField } from '@/components/forms/FormField';
import { Input } from '@/components/ui/input';
import { FieldErrors, UseFormGetValues, UseFormRegister } from 'react-hook-form';
import { VendorRegistrationPayload } from '@/schemas/registration.schema';

interface Step1Props {
    register: UseFormRegister<VendorRegistrationPayload>;
    errors: FieldErrors<VendorRegistrationPayload>;
    showPassword: boolean;
    setShowPassword: React.Dispatch<React.SetStateAction<boolean>>;
    handleNextStep: () => void;
    onLogin: () => void;
    getValues: UseFormGetValues<VendorRegistrationPayload>;
}

export const Step1BasicInfo: React.FC<Step1Props> = ({
    register,
    errors,
    showPassword,
    setShowPassword,
    handleNextStep,
    onLogin,
}) => {
    return (
        <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
            <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Business Name" name="businessName" error={errors.businessName}>
                    <Input
                        {...register('businessName')}
                        placeholder="e.g., Pizza Palace"
                        error={!!errors.businessName}
                    />
                </FormField>

                <FormField label="Owner Name" name="ownerName" error={errors.ownerName}>
                    <Input
                        {...register('ownerName')}
                        placeholder="Your full name"
                        error={!!errors.ownerName}
                    />
                </FormField>
            </div>

            <FormField label="Email Address" name="email" error={errors.email}>
                <Input
                    {...register('email')}
                    type="email"
                    placeholder="owner@pizzapalace.com"
                    autoComplete="email"
                    error={!!errors.email}
                />
            </FormField>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Password */}
                <FormField label="Password" name="password" error={errors.password}>
                    <div className="relative">
                        <Input
                            {...register('password')}
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Create password"
                            error={!!errors.password}
                        />
                        <button
                            type="button"
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            onClick={() => setShowPassword(!showPassword)}
                        >
                            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                    </div>
                </FormField>

                {/* Confirm Password */}
                <FormField label="Confirm Password" name="confirmPassword" error={errors.confirmPassword}>
                    <div className="relative">
                        <Input
                            {...register('confirmPassword')}
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Confirm password"
                            error={!!errors.confirmPassword}
                        />
                        <button
                            type="button"
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            onClick={() => setShowPassword(!showPassword)}
                        >
                            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                    </div>
                </FormField>
            </div>

            <div className="flex justify-between">
                <button
                    type="button"
                    onClick={onLogin}
                    className="text-sm text-primary-600 hover:text-primary-500"
                >
                    Already have an account? Sign in
                </button>

                <button
                    type="button"
                    onClick={handleNextStep}
                    className="btn btn-primary flex items-center"
                >
                    Next Step
                    <ArrowRight className="w-4 h-4 ml-2" />
                </button>
            </div>
        </div>
    );
};
